import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { useCreditStore } from '@/store/creditStore'
import type { CreditTransaction, RechargePackage } from '@/types/ads'

export const CREDIT_BALANCE_QUERY_KEY = ['credits', 'balance'] as const
export const CREDIT_TRANSACTIONS_QUERY_KEY = ['credits', 'transactions'] as const

interface BalanceResponse {
  userId: string
  balance: number
  updatedAt: string
}

// ── useCreditsBalance ──────────────────────────────────────────────────────────

export function useCreditsBalance() {
  const setBalance = useCreditStore((s) => s.setBalance)
  const localBalance = useCreditStore((s) => s.balance)

  const { data, isLoading } = useQuery({
    queryKey: CREDIT_BALANCE_QUERY_KEY,
    queryFn: () =>
      api
        .get<{ success: boolean; data: BalanceResponse }>('/credits/balance')
        .then((r) => r.data.data),
  })

  useEffect(() => {
    if (data?.balance !== undefined) {
      setBalance(data.balance)
    }
  }, [data, setBalance])

  return {
    balance: data?.balance ?? localBalance,
    isLoading,
  }
}

// ── useCreditTransactions ──────────────────────────────────────────────────────

export function useCreditTransactions() {
  const setTransactions = useCreditStore((s) => s.setTransactions)
  const localTransactions = useCreditStore((s) => s.transactions)

  const { data, isLoading } = useQuery({
    queryKey: CREDIT_TRANSACTIONS_QUERY_KEY,
    queryFn: () =>
      api
        .get<{ success: boolean; data: { items: CreditTransaction[] } }>('/credits/transactions')
        .then((r) => r.data?.data?.items ?? []),
  })

  useEffect(() => {
    if (data) {
      setTransactions(data)
    }
  }, [data, setTransactions])

  const resolvedTransactions = data ?? localTransactions
  return {
    transactions: Array.isArray(resolvedTransactions) ? resolvedTransactions : [],
    isLoading,
  }
}

// ── useRecharge ────────────────────────────────────────────────────────────────

interface RechargeResponse {
  balance: number
  transaction: CreditTransaction
}

interface InitializeWelcomeBonusResponse {
  balance: number
  transaction: CreditTransaction
}

/**
 * Initialize welcome bonus on the backend (idempotent).
 * Call this after user login to ensure 250 welcome credits are persisted to the database.
 */
export async function initializeWelcomeBonusBackend(): Promise<void> {
  try {
    await api.post<InitializeWelcomeBonusResponse>('/credits/initialize')
  } catch (err) {
    console.warn('Failed to initialize welcome bonus on backend:', err)
    // Don't throw — local credits already exist, backend will be synced next time balance is fetched
  }
}

export function useRecharge() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (pkg: RechargePackage): Promise<RechargeResponse> => {
      const { data } = await api.post<RechargeResponse>('/credits/recharge', { packageId: pkg.id })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CREDIT_BALANCE_QUERY_KEY })
      qc.invalidateQueries({ queryKey: CREDIT_TRANSACTIONS_QUERY_KEY })
    },
  })
}
